import { useAction } from "convex/react"
import { memo, useEffect, useState } from "react"
import { api } from "~convex/_generated/api"

// Module-level cache — survives re-renders, shared across all instances
const mintCache = new Map<string, { ticker: string } | null>()

interface ResolvedTokenAddressProps {
	mint: string
	onClick?: (mint: string) => void
}

export const ResolvedTokenAddress = memo(function ResolvedTokenAddress({
	mint,
	onClick,
}: ResolvedTokenAddressProps) {
	const [resolved, setResolved] = useState<{ ticker: string } | null>(mintCache.get(mint) ?? null)
	const [loading, setLoading] = useState(!mintCache.has(mint))
	const getTokenInfoByMint = useAction(api.swapActions.getTokenInfoByMint)

	useEffect(() => {
		if (mintCache.has(mint)) {
			setResolved(mintCache.get(mint) ?? null)
			setLoading(false)
			return
		}
		let cancelled = false
		getTokenInfoByMint({ mint })
			.then(info => {
				const entry = info ? { ticker: info.ticker } : null
				mintCache.set(mint, entry)
				if (!cancelled) {
					setResolved(entry)
					setLoading(false)
				}
			})
			.catch(() => {
				mintCache.set(mint, null)
				if (!cancelled) {
					setResolved(null)
					setLoading(false)
				}
			})
		return () => {
			cancelled = true
		}
	}, [mint, getTokenInfoByMint])

	const displayText = resolved ? `$${resolved.ticker}` : `${mint.slice(0, 4)}...${mint.slice(-4)}`
	const label = loading ? `${mint.slice(0, 4)}...${mint.slice(-4)}` : displayText

	if (onClick) {
		return (
			<button
				className="font-semibold text-app-green hover:underline"
				onClick={e => {
					e.stopPropagation()
					e.preventDefault()
					onClick(mint)
				}}
				type="button"
			>
				{label}
			</button>
		)
	}

	return <span className="text-app-green">{label}</span>
})
